import { z } from 'zod';
import { placeKindSchema } from './index.js';
import { VIRGINIA_CITY_ROWS, VIRGINIA_ZIP_ROWS } from './virginia-places.generated.js';
export { VIRGINIA_PLACES_GAZETTEER_YEAR } from './virginia-places.generated.js';
/**
 * The app's own list of Virginia places (ADR-021 section 2 "PL census", #592).
 *
 * Every Virginia city, town and census designated place, and every ZIP code area, with a name and a position, built
 * from US Census Bureau files by `scripts/build-virginia-places.mjs`. `VIRGINIA-PLACES-SOURCE.md` beside this file
 * says where the files came from and what may be done with them. Nothing here is about a person, and nothing here
 * asks anybody anything: the list arrives with the code.
 *
 * NAMES. The Census Bureau's name with its last word ("city", "town" or "CDP") removed, and nothing else changed.
 * Counties are not in the list, so "Fairfax" is the City of Fairfax and "Arlington" is the Census's Arlington place.
 * Where two places share a name (five pairs in 2026) each carries its county: "Rose Hill (Lee County)". A ZIP code
 * area's name is its five digits.
 *
 * Framework-free: no React, no Azure, no clock. The rows are read once, on the first call, not when the module loads.
 */
/**
 * Our code for a place: `place-` and the Census's seven-digit place GEOID (Virginia's begin 51), or `zip-` and the
 * five digits. It fits `PLACE_ID_SHAPE`, so it travels wherever a place id does today.
 *
 * It is told from a Google place id by this exact shape. Google publishes no shape for its ids; the ones this app has
 * handled are long URL-safe base64 text. A code here is 13 or 9 characters, and no base64 text without padding has a
 * length that leaves 1 when divided by 4, which both of those do. Whether a code is a REAL place is a different
 * question, answered by `findVirginiaPlace`.
 */
export const VIRGINIA_PLACE_CODE_SHAPE = /^(?:place-51\d{5}|zip-\d{5})$/;
export const virginiaPlaceCodeSchema = z.string().regex(VIRGINIA_PLACE_CODE_SHAPE, 'Not a Virginia place code');
/** A rounded box a little wider than Virginia on every side. The same numbers are in the build script. */
export const VIRGINIA_BOX = { south: 36.5, north: 39.5, west: -83.7, east: -75.1 };
/** One entry of the list. `lat` and `lng` are the Census's "internal point" for the place, in degrees. */
export const virginiaPlaceSchema = z.object({
    code: virginiaPlaceCodeSchema,
    kind: placeKindSchema,
    name: z.string().min(1).max(80),
    lat: z.number().min(VIRGINIA_BOX.south).max(VIRGINIA_BOX.north),
    lng: z.number().min(VIRGINIA_BOX.west).max(VIRGINIA_BOX.east),
}).strict().superRefine((place, ctx) => {
    // The code says which kind it is; an entry that disagrees with its own code is refused.
    if (place.code.startsWith('zip-') !== (place.kind === 'zip'))
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'The kind does not match the code' });
});
/** How many matches a search gives when the caller names no limit, and the most it will ever give. */
export const VIRGINIA_PLACE_SEARCH_DEFAULT_LIMIT = 10;
export const VIRGINIA_PLACE_SEARCH_MAX_LIMIT = 50;
const searchKey = (text) => text.toLowerCase().replace(/[.']/g, '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
const byKey = (a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
const rowsOf = (block) => block.split('\n').filter((line) => line !== '').map((line) => line.split('|'));
let loaded;
function load() {
    if (loaded)
        return loaded;
    const cities = rowsOf(VIRGINIA_CITY_ROWS).map(([geoid, name, lat, lng]) => ({
        place: Object.freeze({ code: `place-${geoid}`, kind: 'city', name: name ?? '', lat: Number(lat), lng: Number(lng) }),
        key: searchKey((name ?? '').replace(/ \(.*\)$/, '')),
    }));
    const zips = rowsOf(VIRGINIA_ZIP_ROWS).map(([digits, lat, lng]) => ({
        place: Object.freeze({ code: `zip-${digits}`, kind: 'zip', name: digits ?? '', lat: Number(lat), lng: Number(lng) }),
        key: digits ?? '',
    }));
    const all = Object.freeze([...cities, ...zips].map((entry) => entry.place));
    loaded = {
        all,
        byCode: new Map(all.map((place) => [place.code, place])),
        cities: [...cities].sort(byKey),
        zips: [...zips].sort(byKey),
    };
    return loaded;
}
/** The whole list: cities, towns and communities in Census code order, then ZIP code areas in number order. */
export function virginiaPlaces() {
    return load().all;
}
/** The entry for a code, or `undefined` when the code is not in the list (a Google place id never is). */
export function findVirginiaPlace(code) {
    return load().byCode.get(code);
}
/**
 * The places that match what was typed, best first.
 *
 * - Digits only: ZIP code areas whose number BEGINS with them, in number order.
 * - Anything else: cities, towns and communities. First the one whose whole name was typed, then those whose name
 *   BEGINS with the letters, then those where a later word begins with them ("church" finds Falls Church). Each group
 *   is in A to Z order. Capitals, `.`, `'` and `-` make no difference: "st paul" finds "St. Paul".
 *   The county that a shared name carries in brackets is not searched.
 * - Nothing typed, or text that is neither: no matches.
 */
export function searchVirginiaPlaces(typed, options = {}) {
    const wanted = searchKey(typed);
    if (wanted === '')
        return [];
    const asked = options.limit ?? VIRGINIA_PLACE_SEARCH_DEFAULT_LIMIT;
    const limit = Number.isFinite(asked) ? Math.min(Math.max(Math.trunc(asked), 1), VIRGINIA_PLACE_SEARCH_MAX_LIMIT) : VIRGINIA_PLACE_SEARCH_DEFAULT_LIMIT;
    const { cities, zips } = load();
    if (/^\d+$/.test(wanted)) {
        if (options.kind === 'city')
            return [];
        const found = [];
        for (const zip of zips) {
            if (zip.key.startsWith(wanted))
                found.push(zip.place);
            if (found.length === limit)
                break;
        }
        return found;
    }
    if (options.kind === 'zip')
        return [];
    const exact = [];
    const leading = [];
    const laterWord = [];
    for (const city of cities) {
        if (city.key === wanted)
            exact.push(city.place);
        else if (city.key.startsWith(wanted))
            leading.push(city.place);
        else if (city.key.includes(` ${wanted}`))
            laterWord.push(city.place);
    }
    return [...exact, ...leading, ...laterWord].slice(0, limit);
}
//# sourceMappingURL=virginia-places.js.map